import type { PhotographData } from "@qendo/profile/files";
import { useMemo, useState } from "react";
import { Image, Pressable, StyleSheet } from "react-native";
import { RenameFile } from "~/components/form/gallery/RenameFile";
import { ImageBox } from "~/components/layout/gallery/ImageBox";
import { ConfirmationModal } from "~/components/layout/modal/ConfirmationModal";
import { BaseText } from "~/components/layout/text";
import { MenuContent, MenuItem, MenuItemIcon, MenuItemTitle, MenuRoot, MenuTrigger } from "~/components/menu";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { type ApiError, useApi } from "~/providers/ApiProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useGallery } from "~/providers/GalleryProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";

export type FilePickerThumbnailProps = {
    size: number;
    selected?: boolean;
    onPress?: () => void;
} & PhotographData;

const TITLE_HEIGHT = 24;
const ICON_INSET = 4;

export const FilePickerThumbnail = ({ size, selected, onPress, ...item }: FilePickerThumbnailProps) => {
    const { colours, border, background } = useTheme();
    const { img } = useApi();
    const { cannot } = useProfile();
    const { canDestroyMedia, destroyMedia } = useGallery();
    const { addToast, setError } = useFeedback();
    const [preview, setPreview] = useState(false);
    const [rename, setRename] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const image_size = useMemo(() => size - 3.5, [size]);
    const dimensions = useMemo(() => ({ width: size, height: size + TITLE_HEIGHT }), [size]);

    const doDelete = () => {
        if (deleting || !canDestroyMedia(item.id)) {
            return;
        }

        setDeleting(true);

        destroyMedia(item.id)
            .then(() => addToast({ content: "File deleted" }))
            .catch((error: ApiError) => setError(error.response?.data?.message ?? error.message))
            .finally(() => setDeleting(false));
    };

    return (
        <>
            <MenuRoot>
                <MenuTrigger>
                    <Pressable
                        onPress={onPress}
                        style={[styles.pressable, dimensions, selected ? border.selected : border.secondary]}
                    >
                        <Image
                            source={img(item.url)}
                            height={image_size}
                            width={image_size}
                            alt={`${item.name} thumbnail`}
                        />
                        <BaseText style={styles.title} numberOfLines={1}>
                            {item.name}
                        </BaseText>
                        <Only when={selected}>
                            <QendoIcon
                                name="checkmark-circle"
                                color={colours.icon.selected}
                                style={[styles.icon, background.brand]}
                                size={24}
                            />
                        </Only>
                    </Pressable>
                </MenuTrigger>
                <MenuContent>
                    <MenuItem key="file-preview" onSelect={() => setPreview(true)}>
                        <MenuItemTitle>Preview</MenuItemTitle>
                        <MenuItemIcon icon="view-on" ios={{ name: "eye" }} />
                    </MenuItem>
                    <MenuItem key="file-rename" onSelect={() => setRename(true)} disabled={cannot("update", "photos")}>
                        <MenuItemTitle>Rename</MenuItemTitle>
                        <MenuItemIcon icon="edit" ios={{ name: "pencil" }} />
                    </MenuItem>
                    <MenuItem
                        key="file-delete"
                        onSelect={() => setConfirm(true)}
                        destructive
                        disabled={!canDestroyMedia(item.id)}
                    >
                        <MenuItemTitle>Delete</MenuItemTitle>
                        <MenuItemIcon icon="trash" ios={{ name: "trash" }} />
                    </MenuItem>
                </MenuContent>
            </MenuRoot>
            <Only when={preview}>
                <ImageBox image={item} onRequestClose={() => setPreview(false)} />
            </Only>
            <Only when={rename}>
                <RenameFile file={item} onRequestClose={() => setRename(false)} />
            </Only>
            <Only when={confirm}>
                <ConfirmationModal
                    title="Are you sure you want to delete this file?"
                    onConfirm={doDelete}
                    onRequestClose={() => setConfirm(false)}
                    confirmVariant="destructive-quiet"
                    confirmText="Delete file"
                    loading={deleting}
                />
            </Only>
        </>
    );
};

const styles = StyleSheet.create({
    pressable: {
        borderWidth: 2,
        position: "relative",
        marginBottom: 8,
    },
    icon: {
        position: "absolute",
        right: ICON_INSET,
        top: ICON_INSET,
        zIndex: 10,
        width: 24,
        height: 24,
        borderRadius: 30,
    },
    title: {
        fontWeight: 400,
        fontSize: 12,
        lineHeight: TITLE_HEIGHT,
        paddingLeft: 4,
        paddingRight: 4,
        overflow: "hidden",
    },
});
